import { IconPlus, IconSearch } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { AdminUser, ListUsersQuery } from "@/lib/admin-types";
import {
  formatDate,
  formatRelativeTime,
  getUserRoleLabelKey,
  getUserRoles,
  getUserStatus,
} from "@/lib/admin-utils";
import { authClient } from "@/lib/auth-client";
import { UserActions } from "./user-actions";
import { queryKeys } from "@/lib/query-keys";

// Constants
const DEFAULT_PAGE_LIMIT = 50;

type UserListProps = {
  onEditUser?: (user: AdminUser) => void;
  onCreateUser?: () => void;
};

export function UserList({ onEditUser, onCreateUser }: UserListProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState<ListUsersQuery>({
    limit: 50,
    offset: 0,
    sortBy: "createdAt",
    sortDirection: "desc",
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");

  const formatUserRole = (role: string) => {
    const labelKey = getUserRoleLabelKey(role);
    return labelKey ? t(labelKey) : role;
  };

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.admin.users(query),
    queryFn: async () => {
      const response = await authClient.admin.listUsers({ query });
      if (!response.data) {
        throw new Error("Failed to fetch users");
      }
      return response.data;
    },
  });

  const handleSearch = () => {
    setQuery((prev) => ({
      ...prev,
      searchValue: searchTerm || undefined,
      searchField: "email",
      searchOperator: "contains",
      offset: 0, // Reset to first page
    }));
  };

  const handleFilterChange = (status: string) => {
    setFilterStatus(status);
    setQuery((prev) => ({
      ...prev,
      filterField: status === "all" ? undefined : "banned",
      filterValue: status === "all" ? undefined : status === "banned",
      filterOperator: status === "all" ? undefined : "eq",
      offset: 0,
    }));
  };

  const handleSortChange = (sortBy: string) => {
    setQuery((prev) => ({
      ...prev,
      sortBy,
      sortDirection: prev.sortBy === sortBy && prev.sortDirection === "asc" ? "desc" : "asc",
    }));
  };

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-2 text-center">
            <p className="text-muted-foreground text-sm">{t("admin.user.table.loadingError")}</p>
            <Button onClick={() => refetch()} variant="outline">
              {t("admin.user.table.retry")}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search and Filter Controls */}
      <Card>
        <CardHeader>
          <CardTitle>{t("admin.users.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4 flex flex-col gap-4 sm:flex-row">
            <div className="flex flex-1 gap-2">
              <Input
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                placeholder={t("admin.users.search")}
                value={searchTerm}
              />
              <Button onClick={handleSearch} variant="outline">
                <IconSearch className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex gap-2">
              <Select onValueChange={handleFilterChange} value={filterStatus}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("admin.users.filter.all")}</SelectItem>
                  <SelectItem value="active">{t("admin.users.filter.active")}</SelectItem>
                  <SelectItem value="banned">{t("admin.users.filter.banned")}</SelectItem>
                  <SelectItem value="unverified">{t("admin.users.filter.unverified")}</SelectItem>
                </SelectContent>
              </Select>
              <Button className="whitespace-nowrap" onClick={onCreateUser}>
                <IconPlus className="mr-2 h-4 w-4" />
                {t("admin.users.addUser")}
              </Button>
            </div>
          </div>

          {/* Users Table */}
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }, (_, i) => `loading-row-${i}`).map((key) => (
                <div className="h-16 animate-pulse rounded bg-muted" key={key} />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="cursor-pointer" onClick={() => handleSortChange("email")}>
                    {t("admin.user.table.email")}
                  </TableHead>
                  <TableHead className="cursor-pointer" onClick={() => handleSortChange("name")}>
                    {t("admin.user.table.name")}
                  </TableHead>
                  <TableHead>{t("admin.user.table.status")}</TableHead>
                  <TableHead>{t("admin.user.table.role")}</TableHead>
                  <TableHead
                    className="cursor-pointer"
                    onClick={() => handleSortChange("createdAt")}
                  >
                    {t("admin.user.table.created")}
                  </TableHead>
                  <TableHead className="text-right">{t("admin.user.table.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.users?.map((user: AdminUser) => {
                  const status = getUserStatus(user);
                  return (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">{user.email}</TableCell>
                      <TableCell>{user.name || "-"}</TableCell>
                      <TableCell>
                        <Badge variant={status.variant}>{status.label}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {getUserRoles(user).map((role) => (
                            <Badge key={role} variant="outline">
                              {formatUserRole(role)}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="text-sm">{formatDate(user.createdAt)}</div>
                          <div className="text-muted-foreground text-xs">
                            {formatRelativeTime(user.createdAt)}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <UserActions
                          onEdit={() => onEditUser?.(user)}
                          onRefresh={() => refetch()}
                          user={user}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}

          {/* Pagination Info */}
          {data && (
            <div className="mt-4 text-muted-foreground text-sm">
              {t("admin.user.table.showing", {
                start: Math.min(Number(query.offset || 0) + 1, data.total),
                end: Math.min(
                  Number(query.offset || 0) + Number(query.limit || DEFAULT_PAGE_LIMIT),
                  data.total,
                ),
                total: data.total,
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
